import Api from "./Api";
import { existsJSONFile, readJSONFile, writeJSONFile } from "./FileStorage";

/**
 * Catálogo CID-10 no aparelho — baixado UMA vez, guardado em arquivo, servido da memória.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUE ASSIM
 * ─────────────────────────────────────────────────────────────────────────────
 * São 14.496 linhas imutáveis (é a CID-10). Pedir ao servidor a cada abertura do
 * formulário seria ~300 KB por vez, para uma lista que não muda nunca. Então:
 *
 *   1. MEMÓRIA — depois do primeiro uso na sessão, a busca é local e instantânea.
 *   2. ARQUIVO — `tirvumais_data/cid_catalogo.json`, via FileStorage (e não
 *      AsyncStorage: 1,2 MB numa chave só passa do teto por item do Android e
 *      atravanca todas as outras leituras do app).
 *   3. REDE — só quando não há arquivo. Uma vez por instalação.
 *
 * Nada aqui lança: sem arquivo e sem rede, `carregar` devolve lista vazia e a tela
 * explica que o catálogo ainda não foi baixado. O download é disparado no BOOT com
 * token (`garantirCatalogoCidNoAparelho`, no `AuthContext`) — cobre login novo,
 * sessão restaurada e atualização do app pela loja —, para quem chegar à tela
 * offline já encontrar o arquivo. Sem ele, o CID é dispensado.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O CUSTO NO APP, E COMO ELE FICA PEQUENO
 * ─────────────────────────────────────────────────────────────────────────────
 * 14.496 objetos JS com três strings curtas são ~3 MB de heap — aceitável, e só
 * existem depois de a tela de atestado ser aberta. O texto de busca normalizado
 * (`busca`) é pré-calculado UMA vez no carregamento, para a filtragem por tecla não
 * normalizar 14 mil strings a cada caractere digitado. E `buscar` devolve no máximo
 * `LIMITE` resultados: quem renderiza nunca vê mais de 50 linhas.
 */

export type Cid = {
  /** Com ponto, como vai no atestado: "A00.0", "M54.5", "J06". */
  codigo: string;
  descricao: string;
  /** Pré-normalizado para busca: código sem ponto + código com ponto + descrição sem acento. */
  busca: string;
};

const ARQUIVO = "cid_catalogo.json";
const VERSAO_ARQUIVO = 1;
export const LIMITE_RESULTADOS = 50;
export const MINIMO_CARACTERES = 2;

/** "A000" → "A00.0" · "A00" → "A00" · já com ponto → inalterado. */
export const formatarCodigoCid = (bruto: string): string => {
  const s = String(bruto ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  if (!s) return "";
  if (s.includes(".")) return s;
  return s.length > 3 ? `${s.slice(0, 3)}.${s.slice(3)}` : s;
};

const normalizar = (s: string): string =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const montar = (pares: [string, string][]): Cid[] => {
  const lista: Cid[] = [];
  for (const par of pares) {
    if (!Array.isArray(par) || !par[0]) continue;
    const codigo = formatarCodigoCid(par[0]);
    const descricao = String(par[1] ?? "").trim();
    lista.push({
      codigo,
      descricao,
      busca: `${codigo.replace(".", "").toLowerCase()} ${codigo.toLowerCase()} ${normalizar(descricao)}`,
    });
  }
  return lista;
};

// ─── Estado em memória ───────────────────────────────────────────────────────

let memoria: Cid[] | null = null;
let porCodigo: Map<string, Cid> | null = null;
let inflight: Promise<Cid[]> | null = null;

const publicar = (lista: Cid[]) => {
  memoria = lista;
  porCodigo = new Map(lista.map((c) => [c.codigo, c]));
  return lista;
};

/** A lista já está em memória? (síncrono — para a tela decidir o que mostrar sem esperar) */
export const catalogoEmMemoria = (): boolean => !!memoria && memoria.length > 0;

/**
 * Garante o catálogo: memória → arquivo → rede. Idempotente e à prova de boiada
 * (chamadas concorrentes esperam a mesma promessa). Nunca lança.
 */
export const carregarCatalogoCid = async (): Promise<Cid[]> => {
  if (memoria && memoria.length > 0) return memoria;
  if (inflight) return inflight;

  inflight = (async () => {
    // 1) arquivo
    try {
      const salvo = await readJSONFile(ARQUIVO);
      if (
        salvo &&
        salvo.v === VERSAO_ARQUIVO &&
        Array.isArray(salvo.pares) &&
        salvo.pares.length > 0
      ) {
        return publicar(montar(salvo.pares));
      }
    } catch {
      /* arquivo corrompido ou ilegível: segue para a rede */
    }

    // 2) rede — uma vez
    try {
      const r = await Api.geral.catalogoCid();
      const pares = (r?.success && Array.isArray(r.done) ? r.done : []) as [
        string,
        string,
      ][];
      if (pares.length > 0) {
        // Grava os PARES crus (não os objetos montados): metade do tamanho, e a
        // normalização é refeita na leitura — barata, e assim uma mudança futura
        // na regra de busca não exige rebaixar arquivo.
        writeJSONFile(ARQUIVO, { v: VERSAO_ARQUIVO, pares }).catch((e) =>
          console.warn("[CID] não gravou o catálogo em arquivo:", e),
        );
        console.warn(`[CID] catálogo baixado: ${pares.length} códigos`);
        return publicar(montar(pares));
      }
      if (r && !r.success)
        console.warn("[CID] catálogo indisponível:", r.erros ?? r.offline);
    } catch (e) {
      console.warn("[CID] falha ao baixar o catálogo:", e);
    }
    return [];
  })().finally(() => {
    inflight = null;
  });

  return inflight;
};

/**
 * Garante que o catálogo está NO APARELHO — sem carregá-lo na memória.
 *
 * É o gatilho do boot (`AuthContext`, quando há token). Roda em toda abertura do
 * app, então tem que ser barato no caso comum: se o arquivo já existe, é UM
 * `getInfoAsync` e nada mais — nem leitura, nem parse dos 14 mil códigos, que só
 * acontecem quando a tela de atestado precisar. Só baixa quando não há arquivo.
 *
 * ⚠️ Existe por causa de quem JÁ ESTÁ LOGADO. Essa pessoa atualiza o app pela loja e
 * não passa pelo login de novo: o `userData` volta do AsyncStorage e o caminho de
 * `refreshUserData`/`refreshStaticData` não roda. Um gatilho só no login deixaria
 * toda a base instalada sem catálogo até o próximo login — que pode não acontecer.
 */
export const garantirCatalogoCidNoAparelho = async (): Promise<void> => {
  if (memoria && memoria.length > 0) return;
  if (await existsJSONFile(ARQUIVO)) return;
  await carregarCatalogoCid(); // baixa e grava; falha em silêncio
};

/**
 * Busca local. Exige `MINIMO_CARACTERES`; devolve no máximo `LIMITE_RESULTADOS`.
 *
 * Ordem: quem casa pelo CÓDIGO vem antes de quem casa pela descrição — quem digita
 * "m54" quer M54.x no topo, não "hemorragia" no meio. Dentro de cada grupo, a ordem
 * do catálogo (que já vem por código).
 */
export const buscarCids = (termo: string): Cid[] => {
  const lista = memoria ?? [];
  const t = normalizar(termo);
  if (t.length < MINIMO_CARACTERES || lista.length === 0) return [];

  const tCodigo = t.replace(".", "");
  const pareceCodigo = /^[a-z]\d/.test(tCodigo);

  const porCodigoPrefixo: Cid[] = [];
  const porDescricao: Cid[] = [];
  for (const c of lista) {
    if (
      pareceCodigo &&
      c.codigo.replace(".", "").toLowerCase().startsWith(tCodigo)
    ) {
      porCodigoPrefixo.push(c);
    } else if (c.busca.includes(t)) {
      porDescricao.push(c);
    }
    if (porCodigoPrefixo.length >= LIMITE_RESULTADOS) break;
  }
  return [...porCodigoPrefixo, ...porDescricao].slice(0, LIMITE_RESULTADOS);
};

/** O código existe no catálogo? Aceita "M545", "m54.5", "M54.5". `null` = catálogo ausente. */
export const obterCid = (codigo: string): Cid | null => {
  if (!porCodigo) return null;
  return porCodigo.get(formatarCodigoCid(codigo)) ?? null;
};
